/* Jerky selections: one validator for the product form and saved cart data.
   Cart edits update one existing line. Never delete/re-add or choose flavours for a shopper. */
(() => {
  'use strict';
  if (window.SJJerkySelection?.version === '2026-10-22.1') return;
  const PRODUCT = '10195022577885', VARIANT = '50217630662877';
  const flavours = ["Original", "Extra Hot", "Dill Pickle", "Blazin' Buffalo", "Maple Donair", "SPGO", "Sweet Chili"];
  const normalize = value => String(value ?? '').normalize('NFKC').replace(/[\u2018\u2019]/g, "'").replace(/\s+/g, ' ').trim().toLowerCase();
  const canonicalFlavour = value => flavours.find(flavour => normalize(flavour) === normalize(value)) || '';
  const bagKeys = Array.from({length: 6}, (_, index) => `Bag ${index + 1}`);
  const properties = value => Array.isArray(value) ? Object.fromEntries(value.filter(pair => Array.isArray(pair) && pair.length >= 2)) : (value && typeof value === 'object' ? value : {});
  const bagValue = (props, key) => {
    const values = Object.entries(properties(props)).filter(([name]) => normalize(name) === normalize(key)).map(([, value]) => canonicalFlavour(value));
    return values.length && values.every(value => value && value === values[0]) ? values[0] : '';
  };
  const planId = item => String(item?.selling_plan_allocation?.selling_plan?.id || item?.selling_plan_id || '');
  const selection = item => ({bags: bagKeys.map(key => bagValue(item?.properties, key)), plan: planId(item)});
  const complete = item => {
    const chosen = selection(item);
    return chosen.bags.every(Boolean) && /^\d+$/.test(chosen.plan);
  };
  const canonical = value => JSON.stringify(Object.entries(properties(value)).sort(([a], [b]) => a.localeCompare(b)));
  const fingerprint = item => JSON.stringify([String(item.variant_id || item.id), planId(item), canonical(item.properties)]);
  const root = () => window.Shopify?.routes?.root || '/';
  const editors = scope => [...scope.querySelectorAll('sj-jerky-cart-editor')];
  const busy = () => window.sjJerkyCartBusy || window.sjGiftCartBusy || window.sjDirectCheckoutBusy;
  const announce = message => { const target = document.getElementById('status-announcement'); if (target) target.textContent = message; };
  async function request(path, body) {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(root() + path, {method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: {Accept: 'application/json', ...(body ? {'Content-Type': 'application/json'} : {})}, ...(body ? {body: JSON.stringify(body)} : {})});
      const result = await response.json();
      if (!response.ok) {
        const error = new Error(typeof result.description === 'string' ? result.description : 'Your choices could not be saved. Please try again.');
        error.rejected = response.status >= 400 && response.status < 500;
        throw error;
      }
      return result;
    } finally { clearTimeout(timer); }
  }
  function whenReady(element, test, start) {
    element.waiter?.disconnect();
    if (test()) return start();
    element.waiter = new MutationObserver(() => { if (test()) { element.waiter.disconnect(); start(); } });
    element.waiter.observe(element, {childList: true, subtree: true});
  }
  window.SJJerkySelection = {
    version: '2026-10-22.1',
    cartIsComplete: cart => Boolean(cart && Array.isArray(cart.items) && cart.items.every(item => String(item.product_id) !== PRODUCT || complete(item)))
  };

  class JerkyFlavours extends HTMLElement {
    connectedCallback() {
      whenReady(this, () => this.querySelectorAll('select[name^="properties[Bag "]').length === 6 && this.querySelector('[data-sj-selling-plan]'), () => this.start());
    }
    start() {
      this.controller?.abort(); this.observer?.disconnect();
      this.form = document.getElementById(this.dataset.formId);
      if (!(this.form instanceof HTMLFormElement)) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.plans = new Set((this.dataset.planIds || '').split(',').map(id => id.trim()).filter(Boolean));
      this.form.addEventListener('formdata', event => { const plan = this.selectedPlan(); if (plan) event.formData.set('selling_plan', plan.value); }, {signal});
      this.form.addEventListener('change', () => this.refresh(), {signal});
      this.form.addEventListener('input', () => this.refresh(), {signal});
      window.addEventListener('pageshow', () => this.refresh(), {signal});
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this.form, {subtree: true, childList: true, attributes: true, attributeFilter: ['value', 'disabled']});
      this.refresh();
    }
    disconnectedCallback() { this.waiter?.disconnect(); this.controller?.abort(); this.observer?.disconnect(); }
    selectedPlan() {
      const field = this.querySelector('[data-sj-selling-plan]');
      return field && !field.disabled && !field.selectedOptions[0]?.disabled && this.plans.has(field.value) ? field : null;
    }
    state() {
      if (!this.form?.isConnected) return {count: 0, plan: false, valid: false};
      const data = new FormData(this.form);
      let count = 0;
      for (const key of bagKeys) {
        const name = `properties[${key}]`, field = this.querySelector(`select[name="${name}"]`), values = data.getAll(name);
        if (values.length === 1 && canonicalFlavour(values[0]) && field && !field.disabled && !field.selectedOptions[0]?.disabled) count++;
      }
      const selected = this.selectedPlan(), plans = data.getAll('selling_plan').filter(Boolean).map(String), ids = data.getAll('id');
      const plan = Boolean(selected && plans.length === 1 && plans[0] === selected.value);
      return {count, plan, valid: count === 6 && plan && ids.length === 1 && String(ids[0]) === VARIANT && this.dataset.available === 'true'};
    }
    refresh() {
      if (!this.form || !this.isConnected) return;
      const state = this.state(), status = this.querySelector('[data-sj-jerky-count]');
      const message = `${state.count} of 6 bags selected.${state.count < 6 ? ' Choose all six bags to continue.' : (!state.plan ? ' Choose a delivery frequency to continue.' : ' Your subscription selection is ready.')}`;
      if (status && status.textContent !== message) status.textContent = message;
      this.form.querySelectorAll('buy-buttons button[type="submit"]').forEach(button => {
        if (button.getAttribute('aria-busy') !== 'true' && button.disabled !== !state.valid) button.disabled = !state.valid;
      });
      const price = this.selectedPlan()?.selectedOptions[0]?.dataset.price;
      if (price) this.closest('.shopify-section')?.querySelectorAll('[data-sj-subscription-price]').forEach(element => { if (element.textContent !== price) element.textContent = price; });
      if (state.valid) { const error = this.querySelector('[data-sj-jerky-error]'); if (error) error.hidden = true; }
    }
    validate() {
      const state = this.state();
      if (state.valid && this.form.reportValidity()) return true;
      const error = this.querySelector('[data-sj-jerky-error]');
      if (error) { error.hidden = false; error.textContent = state.count < 6 ? 'Choose a flavour for each bag before adding your subscription.' : (!state.plan ? 'Choose your delivery frequency.' : 'This subscription is currently unavailable.'); }
      const missing = [...this.querySelectorAll('select')].find(field => !field.value || field.selectedOptions[0]?.disabled);
      missing?.focus(); this.refresh(); return false;
    }
  }
  if (!customElements.get('sj-jerky-flavours')) customElements.define('sj-jerky-flavours', JerkyFlavours);

  class JerkyCartEditor extends HTMLElement {
    connectedCallback() {
      whenReady(this, () => this.querySelector('[data-sj-item]') && this.querySelectorAll('[data-sj-bag]').length === 6 && this.querySelector('[data-sj-recheck]'), () => this.start());
    }
    start() {
      this.controller?.abort(); this.controller = new AbortController();
      try { this.item = JSON.parse(this.querySelector('[data-sj-item]').textContent); }
      catch (_) { this.say('Your saved choices could not be loaded. Refresh the cart to try again.'); this.setAttribute('data-sj-incomplete', ''); return; }
      this.ready = true; this.pending = null;
      const signal = this.controller.signal;
      this.addEventListener('change', () => { this.dirty = JSON.stringify(this.chosen()) !== this.baselineChoices; this.updateFlags(); this.say(this.dirty ? 'Save your changes before checkout.' : ''); }, {signal});
      this.querySelector('[data-sj-save]').addEventListener('click', () => this.save(), {signal});
      this.querySelector('[data-sj-cancel]').addEventListener('click', () => this.hydrate(this.item), {signal});
      this.querySelector('[data-sj-recheck]').addEventListener('click', () => this.recheck(), {signal});
      this.hydrate(this.item);
      this.removeAttribute('data-sj-pending');
    }
    disconnectedCallback() { this.waiter?.disconnect(); this.controller?.abort(); }
    chosen() { return {bags: [...this.querySelectorAll('[data-sj-bag]')].map(field => canonicalFlavour(field.value)), plan: this.querySelector('[data-sj-cart-plan]')?.value || ''}; }
    hydrate(item) {
      this.item = item; this.baseline = fingerprint(item); this.dataset.lineKey = item.key;
      const saved = selection(item);
      this.querySelectorAll('[data-sj-bag]').forEach((field, index) => { field.value = saved.bags[index]; });
      this.querySelector('[data-sj-cart-plan]').value = saved.plan;
      this.querySelector('[data-sj-cart-consent-wrap]').hidden = Boolean(saved.plan);
      this.querySelector('[data-sj-cart-consent]').checked = false;
      const quantity = this.querySelector('[data-sj-cart-quantity]');
      quantity.hidden = Number(item.quantity) <= 1; quantity.textContent = `These choices apply to all ${item.quantity} boxes in this cart line.`;
      this.baselineChoices = JSON.stringify(this.chosen()); this.dirty = false; this.pending = null;
      this.querySelector('[data-sj-recheck]').hidden = true;
      this.querySelector('details').open = !complete(item);
      this.updateFlags();
      if (!complete(item)) this.explain(); else this.say('');
    }
    updateFlags() {
      this.toggleAttribute('data-sj-incomplete', !complete(this.item));
      this.toggleAttribute('data-sj-dirty', Boolean(this.dirty || this.pending));
      this.querySelector('[data-sj-save]').disabled = Boolean(this.saving || this.pending || (!this.dirty && complete(this.item)));
      this.querySelector('[data-sj-cancel]').hidden = !complete(this.item);
      this.querySelector('[data-sj-cancel]').disabled = Boolean(this.saving || this.pending);
    }
    say(message) { const output = this.querySelector('[data-sj-cart-message]'); if (output) { output.textContent = message; output.hidden = !message; } }
    explain(focus = false) {
      const chosen = this.chosen(), missing = chosen.bags.map((value, index) => value ? null : `Bag ${index + 1}`).filter(Boolean);
      const consentNeeded = !planId(this.item) && !this.querySelector('[data-sj-cart-consent]').checked;
      const message = this.pending ? 'We could not confirm the save. Recheck your saved choices before continuing.' : this.saving ? 'Saving your choices…' : missing.length ? `Choose a flavour for ${missing.join(', ')} below. Your other choices are kept.` : !chosen.plan ? 'Choose a delivery frequency below, then save your choices.' : consentNeeded ? 'Confirm recurring deliveries, then save your choices.' : 'Save your changes below, then continue to checkout.';
      this.querySelector('details').open = true; this.say(message);
      if (focus) {
        this.scrollIntoView({block: 'nearest', behavior: 'auto'});
        const field = missing.length ? this.querySelector(`[data-sj-bag="${chosen.bags.findIndex(value => !value) + 1}"]`) : !chosen.plan ? this.querySelector('[data-sj-cart-plan]') : consentNeeded ? this.querySelector('[data-sj-cart-consent]') : this.querySelector(this.pending ? '[data-sj-recheck]' : '[data-sj-save]');
        field?.focus({preventScroll: true});
      }
    }
    lock(value) { this.saving = value; this.setAttribute('aria-busy', String(value)); this.querySelectorAll('select, input, button').forEach(field => { field.disabled = value || Boolean(this.pending); }); this.querySelector('[data-sj-recheck]').disabled = value; this.updateFlags(); }
    findCurrent(cart) {
      const exact = cart.items.find(item => item.key === this.item.key);
      if (exact) return exact;
      const matches = cart.items.filter(item => String(item.product_id) === PRODUCT && fingerprint(item) === this.baseline);
      if (matches.length === 1) return matches[0];
      throw new Error('This cart line changed elsewhere. Refresh the cart to load its latest choices.');
    }
    async save() {
      if (busy() || this.pending) return;
      const unavailable = [...this.querySelectorAll('[data-sj-bag]')].find(field => field.selectedOptions[0]?.disabled);
      if (unavailable) { this.querySelector('details').open = true; this.say('One of these flavours is currently unavailable. Choose another flavour for that bag.'); unavailable.focus(); return; }
      const chosen = this.chosen(), plan = this.querySelector('[data-sj-cart-plan]');
      if (!chosen.bags.every(Boolean) || !/^\d+$/.test(chosen.plan) || plan.selectedOptions[0]?.disabled || (!planId(this.item) && !this.querySelector('[data-sj-cart-consent]').checked)) { this.explain(true); return; }
      window.sjJerkyCartBusy = true; this.lock(true); this.say('Saving your choices…');
      try {
        const before = await request('cart.js'), current = this.findCurrent(before);
        if (fingerprint(current) !== this.baseline) throw new Error('This box changed elsewhere. Refresh the cart before saving.');
        const updated = {...properties(current.properties)};
        for (const key of Object.keys(updated)) if (bagKeys.some(bag => normalize(bag) === normalize(key))) delete updated[key];
        chosen.bags.forEach((value, index) => { updated[bagKeys[index]] = value; });
        // A stable private identity prevents Shopify from merging two different boxes while editing.
        updated._sj_jerky_cart_ref = updated._sj_jerky_cart_ref || globalThis.crypto?.randomUUID?.() || (Date.now() + '-' + Math.random().toString(36).slice(2));
        // Quantity is read afresh; using line (not variant id) is required for selling_plan changes.
        const section = document.querySelector('.shopify-section--main-cart');
        const sectionId = section?.id?.replace(/^shopify-section-/, '');
        const body = {line: before.items.indexOf(current) + 1, quantity: current.quantity, properties: updated, selling_plan: Number(chosen.plan)};
        if (sectionId) { body.sections = [sectionId]; body.sections_url = location.pathname + location.search; }
        const desired = {...current, properties: updated, selling_plan_allocation: null, selling_plan_id: chosen.plan};
        this.pending = {fingerprint: fingerprint(desired), quantity: current.quantity, sectionId}; this.updateFlags();
        let result;
        try { result = await request('cart/change.js', body); }
        catch (error) {
          if (error.rejected) { this.pending = null; throw error; }
          result = await request('cart.js');
        }
        await this.confirm(result);
      } catch (error) {
        this.say(this.pending ? 'We could not confirm the save. Please recheck your saved choices.' : error.message || 'Your choices could not be saved. Please try again.');
        this.querySelector('[data-sj-recheck]').hidden = !this.pending;
      } finally { window.sjJerkyCartBusy = false; this.lock(false); }
    }
    async recheck() {
      if (busy() || !this.pending) return;
      window.sjJerkyCartBusy = true; this.lock(true);
      try { await this.confirm(await request('cart.js')); }
      catch (_) { this.say('The save is still unconfirmed. Refresh the cart to see the latest saved choices.'); }
      finally { window.sjJerkyCartBusy = false; this.lock(false); }
    }
    async confirm(cart) {
      const op = this.pending;
      const matches = cart.items.filter(item => String(item.product_id) === PRODUCT && fingerprint(item) === op.fingerprint && Number(item.quantity) === Number(op.quantity));
      if (matches.length !== 1) throw new Error('The cart update could not be confirmed.');
      const saved = matches[0], oldKey = this.item.key;
      this.hydrate(saved); this.say('Choices saved. You can continue to checkout.');
      // Refresh all rendered copies from Shopify, including price, plan and changed line keys.
      const section = op.sectionId && document.getElementById('shopify-section-' + op.sectionId);
      if (section) {
        try {
        let html = cart.sections?.[op.sectionId];
        if (!html) { const result = await request('cart?sections=' + encodeURIComponent(op.sectionId)); html = result[op.sectionId]; }
        if (html) { const doc = new DOMParser().parseFromString(html, 'text/html'), fresh = doc.getElementById(section.id); if (!fresh) throw new Error('Cart section missing.'); section.innerHTML = fresh.innerHTML; }
        else throw new Error('Cart display refresh needed.');
        } catch (_) {
          // The save was confirmed. Reload only the display; never repeat the write.
          announce('Choices saved. Updating your cart…'); location.reload(); return;
        }
      }
      editors(document).forEach(editor => { if (editor.ready && editor.item.key === oldKey) editor.hydrate(saved); });
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {bubbles: true}));
      announce('Jerky subscription choices saved. You can continue to checkout.');
    }
  }
  if (!customElements.get('sj-jerky-cart-editor')) customElements.define('sj-jerky-cart-editor', JerkyCartEditor);

  function checkCart(scope, event) {
    if (!scope) return true;
    const invalid = editors(scope).find(editor => !editor.ready || !complete(editor.item) || editor.dirty || editor.pending);
    if (!invalid && !busy()) return true;
    event.preventDefault(); event.stopImmediatePropagation();
    if (invalid?.ready) invalid.explain(true); else announce(busy() ? 'Please wait for your cart update to finish.' : 'Loading your saved subscription choices.');
    return false;
  }
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const checkout = event.submitter?.name === 'checkout' || form.matches('.cart-form, cart-drawer form.buy-buttons');
    if (checkout && !checkCart(form.closest('.cart, cart-drawer'), event)) return;
    const picker = form.querySelector('sj-jerky-flavours');
    if (!picker && String(new FormData(form).get('id')) !== VARIANT) return;
    if (picker && picker.validate()) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (!picker) location.assign(root() + 'products/jerky-subscription-pick-any-6');
  }, true);
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest('[name="checkout"], a[href*="/checkout"], line-item-quantity a');
    if (!target) return;
    const scope = target.closest('.cart, cart-drawer');
    if (target.closest('line-item-quantity')) { if (scope && busy()) { event.preventDefault(); event.stopImmediatePropagation(); announce('Please wait for your cart update to finish.'); } }
    else checkCart(scope, event);
  }, true);
})();
